import Image from "next/image";
import Link from "next/link";
import davidChangHomeImage from "@assets/images/teams/david-chang.1024x1024.jpg";

export interface TeamMemberCard {
  name: string;
  role: string;
  experience: string;
  description: string;
  imagePath: string;
  imageAlt: string;
  href: string;
  tags?: string[];
}

interface TeamGridProps {
  members: TeamMemberCard[];
  intro?: string;
}

const localImagePathMap: Record<string, string> = {
  "@assets/images/teams/david-chang.1024x1024.jpg": davidChangHomeImage.src,
};

function resolveTeamImagePath(imagePath: string): string {
  if (!imagePath.startsWith("@assets/")) {
    return imagePath;
  }

  const resolvedImagePath = localImagePathMap[imagePath];
  if (!resolvedImagePath) {
    throw new Error(`TeamGrid: missing local image mapping for "${imagePath}".`);
  }

  return resolvedImagePath;
}

export default function TeamGrid({ members, intro }: TeamGridProps) {
  if (!Array.isArray(members) || members.length === 0) {
    throw new Error("TeamGrid: `members` array is empty or invalid.");
  }

  return (
    <div className="team-grid-wrap">
      {intro ? <p className="team-grid-intro">{intro}</p> : null}
      <div className={`team-grid${members.length === 4 ? " team-grid--4" : ""}`}>
        {members.map((member) => (
          <Link
            key={member.name}
            href={member.href}
            className="team-card"
            aria-label={`Profil ${member.name} ansehen`}
          >
            <div className="team-card-photo">
              <Image
                src={resolveTeamImagePath(member.imagePath)}
                alt={member.imageAlt}
                width={400}
                height={400}
                sizes="(min-width: 980px) 320px, 100vw"
              />
            </div>
            <div className="team-card-body">
              <span className="team-card-exp">{member.experience}</span>
              <h4 className="team-card-name">{member.name}</h4>
              <p className="team-card-role">{member.role}</p>
              <p className="team-card-desc">{member.description}</p>
              {member.tags && member.tags.length > 0 ? (
                <ul className="team-card-tags">
                  {member.tags.map((tag) => (
                    <li key={tag}>{tag}</li>
                  ))}
                </ul>
              ) : null}
              <span className="team-card-cta">
                Profil ansehen
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
